import React, { useEffect, useRef, useState } from "react";
import DashboardContent from "../../components/DashboardContent";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import RadioGroup from "../../components/RadioGroup";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setPatientNarrativeName } from "../../store/slices/patientNarrativeSlice";
import { CsrAPI } from "../../apis/CsrAPI";
import questionsData from "../../assets/json/patient_narrative_questions_data.json";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";

// Same wording CSR uses for an unfilled required field (see LSForm.js). The
// name input deliberately has no `required` attribute: browsers disagree on
// whether and how they surface the native bubble, so the check runs in
// handleSummary and renders as an inline message instead.
const REQUIRED_FIELD_MESSAGE = "This field is required.";

const PNSummary = () => {
  // The route param is still named `studyId` (shared with the rest of the
  // `pn/:studyId/...` route tree) but it is a PatientNarrativeModel id, not
  // a CSR StudyModel id - renamed locally so nothing downstream confuses
  // the two. See useEffect below: this component talks to
  // `/patient-narratives/...` exclusively, never `/studies/...`.
  const { studyId: patientNarrativeId } = useParams();
  const { modalWindow } = useModalWindow();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const studyNameRef = useRef();
  const errRef = useRef();

  const [studyName, setStudyName] = useState("");
  // The real, saved name of an existing Patient Narrative - fetched below,
  // never faked. Distinct from `useCsr().currentStudyName`, which is
  // populated from a CSR StudyModel lookup and would never resolve for a
  // Patient Narrative id.
  const [existingName, setExistingName] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [nameError, setNameError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Only meaningful while creating a new Patient Narrative - once created,
  // the link is fixed (editing it later goes through
  // CsrAPI.updatePatientNarrativeLink, not this page).
  const [linkMode, setLinkMode] = useState("standalone");
  const [selectedCsrId, setSelectedCsrId] = useState("");
  const [eligibleCsrs, setEligibleCsrs] = useState([]);

  // For an EXISTING Patient Narrative (returning to this page after
  // creation) - read-only display of whatever was actually persisted at
  // creation time, so the choice doesn't just disappear on revisit.
  const [existingLinkedCsrId, setExistingLinkedCsrId] = useState(null);
  const [existingLinkedCsrName, setExistingLinkedCsrName] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Synchronous guard against a double-submit: a ref updates immediately,
  // before React re-renders, so two clicks arriving in the same tick (e.g.
  // a fast double-click, before `isCreating` state has flushed) cannot both
  // pass this check the way two reads of state could.
  const isCreatingRef = useRef(false);

  // Reopening an existing Patient Narrative only ever reads its saved name -
  // creation happens exclusively inside handleSummary, triggered by an
  // explicit Next-button submit, never as a side effect of mounting here.
  useEffect(() => {
    if (!patientNarrativeId) return undefined;
    let cancelled = false;

    CsrAPI.getPatientNarrative({ patientNarrativeId })
      .then(response => {
        if (cancelled || !mountedRef.current) return;
        setExistingName(response.name);
        dispatch(setPatientNarrativeName(response.name));
        setExistingLinkedCsrId(response.linked_csr_id || null);
      })
      .catch(error => {
        if (cancelled || !mountedRef.current) return;
        if (error.response?.status === 403) {
          setShowRedirect(true);
        } else if (!error?.response) {
          setErrMsg("No Server Response");
        } else {
          setErrMsg("Failed to load this Patient Narrative");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId, dispatch]);

  // Convenience list for the "Select CSR project" dropdown below - only
  // fetched while creating a new Patient Narrative. Non-blocking: standalone
  // creation must still work even if this fails, and the backend
  // revalidates any linked_csr_id server-side regardless of what this list
  // contained.
  useEffect(() => {
    if (patientNarrativeId) return undefined;
    let cancelled = false;

    CsrAPI.getEligibleCsrsForPatientNarrative()
      .then(response => {
        if (cancelled || !mountedRef.current) return;
        setEligibleCsrs(response?.studies || []);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId]);

  // Resolves the linked CSR's display name for an EXISTING Patient
  // Narrative - non-blocking, same as everywhere else this session reads a
  // linked CSR's name (ProjectConventions.js). A failure here just leaves
  // the id-only fallback below; it never blocks the page.
  useEffect(() => {
    if (!existingLinkedCsrId) return undefined;
    let cancelled = false;

    CsrAPI.getOneStudy({ studyId: existingLinkedCsrId })
      .then(study => {
        if (cancelled || !mountedRef.current) return;
        setExistingLinkedCsrName(study?.name || "");
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [existingLinkedCsrId]);

  useEffect(() => {
    if (!errMsg) {
      studyNameRef.current?.focus();
    }
  }, [errMsg]);

  useEffect(() => {
    setErrMsg("");
  }, [patientNarrativeId]);

  const handleStudyNameChange = event => {
    setStudyName(event.target.value);
  };

  useEffect(() => {
    setErrMsg("");
    setNameError("");
  }, [studyName]);

  // Gates the Next button while creating. Keyed on `patientNarrativeId`, not
  // on `existingName`: reopening an existing Patient Narrative leaves
  // `studyName` empty (the saved name renders read-only from
  // `existingName`), and that path only navigates - it never POSTs - so it
  // must stay enabled, including while the name is still being fetched.
  const isNameMissing = !patientNarrativeId && !studyName.trim();

  const handleSummary = async event => {
    event.preventDefault();
    if (isCreatingRef.current) return;

    if (!studyName.trim()) {
      setNameError(REQUIRED_FIELD_MESSAGE);
      studyNameRef.current?.focus();
      return;
    }

    isCreatingRef.current = true;
    setIsCreating(true);

    try {
      const isConfirmed = await modalWindow({
        title: "Note to the writer",
        message:
          "Note to the writer: Please be prepared by having available a copy of the LISTING and CIOMS. ",
        confirmButtonLabel: "OK",
      });

      if (isConfirmed) {
        setAjaxLoading(true);

        try {
          const response = await CsrAPI.createPatientNarrative({
            name: studyName,
            linkedCsrId: linkMode === "linked" ? selectedCsrId : null,
          });
          const newPatientNarrativeId = response?.patient_narrative_id;
          setErrMsg("");
          dispatch(setPatientNarrativeName(response?.name || studyName));

          if (mountedRef.current && newPatientNarrativeId) {
            navigate("/pn/" + newPatientNarrativeId + "/file-upload");
          }
        } catch (error) {
          if (mountedRef.current) {
            if (isProduction) {
              Sentry.captureException(error);
            }
            if (!error?.response) {
              setErrMsg("No Server Response");
            } else if (error.response?.status === 401) {
              setErrMsg("Invalid user credentials");
            } else if (error.response?.status === 403) {
              setShowRedirect(true);
            } else {
              setErrMsg("Failed to create a new Patient Narrative");
            }
            errRef.current?.focus();
          }
        } finally {
          if (mountedRef.current) {
            setAjaxLoading(false);
          }
        }
      }
    } catch (modalError) {
      if (mountedRef.current) {
        setErrMsg("An unexpected error occurred");
        errRef.current?.focus();
      }
    } finally {
      isCreatingRef.current = false;
      if (mountedRef.current) {
        setIsCreating(false);
      }
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-summary csr-summary__wrapper"}>
        <form id={"pn-summary-form"} onSubmit={handleSummary}>
          <span
            ref={errRef}
            className={errMsg ? "errmsg" : "offscreen"}
            aria-live="assertive">
            {errMsg}
          </span>
          <div className={"form-component csr-summary__form-component"}>
            <label htmlFor="studyname">
              Patient Narrative Name
              <span className={"questions-content__required-asterisk"}> *</span>
            </label>
            {existingName ? (
              <input
                type="text"
                id="studyname"
                ref={studyNameRef}
                autoComplete="off"
                value={existingName}
                aria-invalid={"false"}
                placeholder={"Patient Narrative name"}
                className={"input-text"}
                disabled={true}
              />
            ) : (
              <input
                type="text"
                id="studyname"
                ref={studyNameRef}
                autoComplete="off"
                onChange={handleStudyNameChange}
                value={studyName}
                aria-invalid={nameError ? "true" : "false"}
                aria-describedby={nameError ? "studyname-error" : undefined}
                placeholder={"Patient Narrative name"}
                className={"input-text"}
                disabled={isCreating}
              />
            )}
            {nameError && (
              <div
                id={"studyname-error"}
                className={"completion-error"}
                aria-live={"polite"}>
                {nameError}
              </div>
            )}
          </div>

          {!existingName && (
            <div className={"questions-content__container"}>
              <div className={"questions-content__item"}>
                <div className={"questions-content__label"}>
                  <span className={"questions-content__label-text"}>
                    {questionsData.labels.link_to_csr}
                  </span>
                  <QuestionItemInfo
                    questionName={"link_to_csr"}
                    notes={questionsData.questionsInfo}
                  />
                </div>
                <RadioGroup
                  name={"link_to_csr"}
                  value={linkMode}
                  onChange={value => {
                    setLinkMode(value);
                    if (value === "standalone") setSelectedCsrId("");
                  }}
                  options={questionsData.choices.link_to_csr}
                  disabled={isCreating}
                />
                {linkMode === "linked" && (
                  <select
                    className={"input-text"}
                    aria-label={"Select CSR project"}
                    value={selectedCsrId}
                    onChange={event => setSelectedCsrId(event.target.value)}
                    disabled={isCreating}
                    required>
                    <option value="">Select a CSR project...</option>
                    {eligibleCsrs.map(study => (
                      <option key={study.study_id} value={study.study_id}>
                        {study.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {existingName && (
            <div className={"questions-content__container"}>
              <div className={"questions-content__item"}>
                <div className={"questions-content__label"}>
                  <span className={"questions-content__label-text"}>
                    {questionsData.labels.link_to_csr}
                  </span>
                  <QuestionItemInfo
                    questionName={"link_to_csr"}
                    notes={questionsData.questionsInfo}
                  />
                </div>
                {/* Read-only: the link is fixed at creation time (see the
                    state comment above) - this just makes sure the choice
                    the user already made is still visible on revisit,
                    instead of silently disappearing. */}
                <span className={"questions-content__label-text"}>
                  {existingLinkedCsrId
                    ? `Linked to ${existingLinkedCsrName || existingLinkedCsrId}`
                    : "Standalone"}
                </span>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Type the name of your Patient Narrative"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={false}
      nextButton={{
        buttonTitle: "Files Upload",
        buttonFormAction: patientNarrativeId ? "" : "pn-summary-form",
        buttonGoToAction: patientNarrativeId
          ? "/pn/" + patientNarrativeId + "/file-upload"
          : "",
        disabled:
          isCreating ||
          isNameMissing ||
          (linkMode === "linked" && !selectedCsrId),
      }}
    />
  );
};

export default PNSummary;
